import React, { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChatCircleDots, PaperPlaneRight, X, CircleNotch, Sparkle } from '@phosphor-icons/react'
import { toast } from 'sonner'
import { workflowsApi } from '@/lib/api'
import type { AIChatMessage } from '@/types/workflow'

interface CanvasChatPanelProps {
  /** Live canvas state — sent to the model so it edits the current workflow. */
  nodes: any[]
  edges: any[]
  workflowName: string
  workflowDescription: string
  /** Seed transcript (the original generation conversation, continued here). */
  initialMessages: AIChatMessage[]
  /** Apply the model's updated canvas (full re-generate + re-layout). */
  onApply: (nodes: any[], edges: any[]) => void
  /** Lift the updated transcript so the builder can persist it on save. */
  onMessagesChange: (messages: AIChatMessage[]) => void
}

const CanvasChatPanel: React.FC<CanvasChatPanelProps> = ({
  nodes, edges, workflowName, workflowDescription, initialMessages, onApply, onMessagesChange,
}) => {
  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState<AIChatMessage[]>(initialMessages || [])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const seededRef = useRef(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  // Seed once from the persisted transcript when it arrives (async load).
  useEffect(() => {
    if (!seededRef.current && initialMessages && initialMessages.length > 0) {
      setMessages(initialMessages)
      seededRef.current = true
    }
  }, [initialMessages])

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages, loading])

  const send = async () => {
    const text = input.trim()
    if (!text || loading) return
    const userMsg: AIChatMessage = { role: 'user', content: text }
    const next = [...messages, userMsg]
    setMessages(next)
    setInput('')
    setLoading(true)
    try {
      const res = await workflowsApi.aiEditWorkflow({
        messages: next
          .filter(m => m.role === 'user' || m.role === 'assistant')
          .map(m => ({ role: m.role, content: m.content })),
        nodes,
        edges,
        workflow_name: workflowName || undefined,
        workflow_description: workflowDescription || undefined,
      })
      const assistantMsg: AIChatMessage = { role: 'assistant', content: res.data.message }
      const updated = [...next, assistantMsg]
      setMessages(updated)
      onMessagesChange(updated)
      if (Array.isArray(res.data.nodes) && res.data.nodes.length > 0) {
        onApply(res.data.nodes, res.data.edges || [])
        toast.success('Workflow updated. Review and save.')
      }
    } catch (error: any) {
      const detail = error?.response?.data?.detail || 'Failed to edit workflow'
      toast.error(detail)
      // Roll back the optimistic user message on failure.
      setMessages(messages)
    } finally {
      setLoading(false)
    }
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      send()
    }
  }

  return (
    <>
      {/* Floating launcher */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          title="Edit with AI"
          className="absolute bottom-6 right-6 z-20 flex items-center gap-2 rounded-full bg-[#5e6ad2] px-4 py-3 text-white shadow-lg hover:bg-[#4e5ac2] transition-colors"
        >
          <ChatCircleDots size={20} weight="fill" />
          <span className="text-sm font-medium">Edit with AI</span>
        </button>
      )}

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className="absolute bottom-6 right-6 z-20 flex h-[520px] w-[380px] flex-col overflow-hidden rounded-xl border border-border bg-card shadow-2xl"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <div className="flex items-center gap-2">
                <Sparkle size={18} weight="fill" className="text-[#5e6ad2]" />
                <span className="text-sm font-emphasis">Edit with AI</span>
              </div>
              <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground">
                <X size={18} />
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-3">
              {messages.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  Ask me to change this workflow — e.g. “add a 3-day delay before email 2”,
                  “branch on reply sentiment”, or “make the tone friendlier”. I’ll update the canvas.
                </p>
              )}
              {messages.filter(m => m.role !== 'system').map((m, i) => (
                <div
                  key={i}
                  className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}
                >
                  <div
                    className={
                      'max-w-[85%] whitespace-pre-wrap rounded-lg px-3 py-2 text-sm ' +
                      (m.role === 'user'
                        ? 'bg-[#5e6ad2] text-white'
                        : 'bg-muted text-foreground')
                    }
                  >
                    {m.content}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <CircleNotch size={14} className="animate-spin" /> Updating workflow…
                </div>
              )}
            </div>

            {/* Input */}
            <div className="border-t border-border p-3">
              <div className="flex items-end gap-2">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKeyDown}
                  rows={2}
                  placeholder="Describe a change…"
                  disabled={loading}
                  className="flex-1 resize-none rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-[#5e6ad2]"
                />
                <button
                  onClick={send}
                  disabled={loading || !input.trim()}
                  className="rounded-md bg-[#5e6ad2] p-2 text-white disabled:opacity-50 hover:bg-[#4e5ac2]"
                >
                  <PaperPlaneRight size={18} weight="fill" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

export default CanvasChatPanel
